import React, { useEffect, useState, useRef, useMemo } from "react";
import { mergeStyles } from "../../utils/utilStyles";

const baseStyle = {
  backgroundColor: "bg-white",
  textColor: "text-ov-gray-700",
  titleColor: "#6B7280",
  disabledTextColor: "text-ov-gray-700",
  disabledBackgroundColor: "bg-ov-gray-100",
};

const DropdownField = ({
  title,
  options,
  selectedOption,
  defaultOption,
  onChange,
  disabled = false,
  openDirection = "down",
  overrideStyle = {},
  firstOptionOnOpen = null, // New prop
  errorMessage = "",
  className = "",
  prefixIcon = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const itemRefs = useRef([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [lastKeyPressTime, setLastKeyPressTime] = useState(0);
  const searchTimeout = useRef(null);

  const mergedStyle = mergeStyles(baseStyle, overrideStyle);

  const handleOptionClick = (index) => {
    if (disabled) return;

    setSelectedIndex(index);

    if (index === -1 && defaultOption) {
      onChange(defaultOption.option ? defaultOption.value : defaultOption);
      setIsOpen(false);
      return;
    }

    var option = options[index].value ? options[index].value : options[index];
    onChange(option);
    setIsOpen(false);
  };

  // Scroll to the selected item or the firstOptionOnOpen when the dropdown is opened
  const scrollToSelected = (scrollTo = null) => {
    if (dropdownRef.current) {
      const indexToScroll =
        scrollTo != null ? scrollTo : firstOptionOnOpen !== null ? firstOptionOnOpen : selectedIndex;
      if (itemRefs.current[indexToScroll]) {
        dropdownRef.current.scrollTop = itemRefs.current[indexToScroll].offsetTop;
      }
    }
  };

  // close dropdown when clicked outside
  const handleClickOutside = (event) => {
    if (event.target.closest(".my-drop-down") === null) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (selectedOption === null || selectedOption === "") {
      setSelectedIndex(-1);
    } else {
      const index = options.findIndex((option) => option.value === selectedOption);
      if (index !== -1) {
        setSelectedIndex(index);
      } else {
        setSelectedIndex(-1);
      }
    }

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [selectedOption, options]);

  useEffect(() => {
    if (isOpen) {
      scrollToSelected();
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    const currentTime = new Date().getTime();

    // If the time between key presses exceeds 1 second, reset the search term
    if (currentTime - lastKeyPressTime > 1000) {
      setSearchTerm("");
    }
    setLastKeyPressTime(currentTime);

    if (!isOpen && e.key === "Enter") {
      setIsOpen(true);
      return;
    }

    if (!isOpen) {
      return;
    }

    // Prevent default browser scrolling
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
    }

    if (e.key === "ArrowDown") {
      const moveToIndex = selectedIndex === -1 ? 0 : (selectedIndex + 1) % options.length;
      setSelectedIndex(moveToIndex);
      scrollToSelected(moveToIndex);
    } else if (e.key === "ArrowUp") {
      const moveToIndex =
        selectedIndex === -1 ? options.length - 1 : (selectedIndex - 1 + options.length) % options.length;
      setSelectedIndex(moveToIndex);
      scrollToSelected(moveToIndex);
    } else if (e.key === "Enter") {
      handleOptionClick(selectedIndex);
    } else if (/^[a-zA-Z0-9]$/.test(e.key)) {
      // Handle type-to-search
      const newSearchTerm = searchTerm + e.key.toLowerCase();
      setSearchTerm(newSearchTerm);

      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      // Find the first option that matches the search term
      const matchedIndex = options.findIndex((option) => option.option.toLowerCase().startsWith(newSearchTerm));

      if (matchedIndex !== -1) {
        setSelectedIndex(matchedIndex);
        scrollToSelected(matchedIndex);
      }

      searchTimeout.current = setTimeout(() => {
        setSearchTerm("");
      }, 1000);
    }
  };

  return (
    <div className="min-w-20 my-drop-down relative w-full text-[12px] md:text-[14px] lg:text-[16px]">
      {selectedIndex >= 0 && title && (
        <p
          className={`text-[12px] ${
            disabled ? mergedStyle.disabledTextColor : mergedStyle.titleColor
          } absolute left-1 transition-all rounded-lg
            top-0 -translate-y-1/2 z-30 px-2 ${
              disabled ? mergedStyle.disabledBackgroundColor : mergedStyle.backgroundColor
            }
          `}
        >
          {title}
        </p>
      )}
      <div
        className={`w-full h-10 font-modal-inputs text-[12px] md:text-[14px] border border-gray-300 focus:outline-none focus:border-gray-500 ${
          errorMessage
            ? "border-b border-b-red-500 focus:border-b-red-500"
            : selectedOption &&
              defaultOption &&
              selectedOption !== (defaultOption.option ? defaultOption.value : defaultOption)
            ? "border-b border-b-ov-green focus:border-b-ov-green"
            : ""
        } rounded-md p-2 cursor-pointer flex items-center gap-2 ${
          disabled ? "bg-ov-disabled-input pointer-events-none" : "bg-white pointer-events-auto"
        } ${className}`}
        tabIndex={0}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
      >
        {prefixIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <img src={prefixIcon} alt="prefix icon" className="size-4" />
          </div>
        )}
        {selectedIndex === -1 && defaultOption && (
          <span className={`text-ov-gray-400 ${prefixIcon ? "pl-6" : ""}`}>
            {defaultOption.option ? defaultOption.option : defaultOption}
          </span>
        )}
        {
          selectedIndex === -1 && !defaultOption && (
            <span className={`text-ov-gray-400 ${prefixIcon ? "pl-6" : ""}`}>
              {title}
            </span>
          )
        }
        <span className={`${prefixIcon ? "pl-6" : ""}`}>
          {selectedIndex >= 0 && selectedIndex < options.length && options[selectedIndex].option
            ? options[selectedIndex].option
            : options[selectedIndex]}
        </span>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex justify-center items-center ">
          <svg
            className={`size-4 md:size-5 lg:size-6 transition-transform duration-500 ease-in-out transform ${
              isOpen ? "rotate-180" : ""
            }`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>

      {isOpen && (
        <ul
          ref={dropdownRef}
          className={`absolute border border-gray-300  rounded-lg bg-white mt-1 z-50 w-full  max-h-40 overflow-auto ${
            openDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          {defaultOption && (
            <li
              className={`font-modal-inputs text-[12px] md:text-[14px] p-2 hover:bg-gray-100 cursor-pointer ${
                selectedIndex == -1 ? "bg-slate-100" : ""
              }`}
              onClick={() => handleOptionClick(-1)}
            >
              {defaultOption.option ? defaultOption.option : defaultOption}
            </li>
          )}
          {options.map((option, index) => (
            <li
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}
              className={`font-modal-inputs text-[12px] md:text-[14px] p-2 hover:bg-gray-100 cursor-pointer ${
                selectedIndex == index ? "bg-slate-100" : ""
              }`}
              onClick={() => handleOptionClick(index)}
            >
              {option.option ? option.option : option}
            </li>
          ))}
        </ul>
      )}

      {errorMessage && <p className={`absolute text-[8px] sm:text-[12px] text-red-500`}>{errorMessage}</p>}
    </div>
  );
};

export default DropdownField;
